import React from 'react';
import style from './index.module.scss';
import {
    Button,
    DatePicker,
    Form,
    Input,
    message,
    Modal,
    ModalProps,
    Select,
} from 'antd';
import {useForm} from 'antd/es/form/Form';
import {DeleteTwoTone, PlusSquareOutlined} from '@ant-design/icons';
import useRequestFn from 'Utils/Hooks/useRequestFn';
import {errorMessage} from 'Utils/common';
import {useAtomValue} from 'jotai';
import {taskListAtom} from '../../store';
import {PlanListItem, PlanSaveParams, savePlan} from '../../../service';
import dayjs from 'dayjs';

interface AddPlanModalProps extends ModalProps {
    initValue?: PlanListItem; //当前计划列表数据
    refreshData: () => void; //更新函数
    flag?: 'add' | 'edit';
}

export default function AddPlanModal({
    initValue,
    refreshData,
    flag = 'add',
    ...restProps
}: AddPlanModalProps) {
    const {TextArea} = Input;
    const {Option} = Select;
    const [form] = useForm();

    const taskList = useAtomValue(taskListAtom);
    const [save] = useRequestFn(savePlan);

    async function handleFinish(data: PlanSaveParams) {
        const params = {
            ...data,
            id: flag === 'edit' ? initValue?.id : '',
        };

        try {
            await save({...params});
            message.success(flag === 'add' ? '添加成功' : '修改成功');
            refreshData();
        } catch (e) {
            message.error(errorMessage(e));
        }
    }

    const layout = {
        labelCol: {span: 7},
        wrapperCol: {span: 14, offset: 1},
    };

    const formItemLayoutWithOutLabel = {
        wrapperCol: {
            span: 14,
            offset: 8,
        },
    };

    return (
        <Modal
            title={flag === 'add' ? '新增调度计划' : '编辑调度计划'}
            maskClosable={false}
            onOk={form.submit}
            {...restProps}
            width={880}
            className={style.root}>
            <Form
                form={form}
                onFinish={handleFinish}
                className={style.form}
                labelCol={layout.labelCol}
                wrapperCol={layout.wrapperCol}
                scrollToFirstError
                autoComplete="off"
                initialValues={{
                    task: flag === 'add' ? [{id: null}] : initValue!.taskList,
                }}>
                <Form.Item
                    name="name"
                    label="名称："
                    initialValue={flag === 'add' ? '' : initValue?.name}>
                    <Input placeholder="输入调度计划名称" maxLength={500} />
                </Form.Item>

                <Form.Item
                    name="code"
                    label="编码"
                    initialValue={flag === 'add' ? '' : initValue?.code}
                    rules={[
                        {
                            required: true,
                            message: `请输入调度计划名称的半角英文编码`,
                        },
                    ]}>
                    <Input
                        disabled={flag === 'edit'}
                        placeholder="输入调度计划名称的半角英文编码"
                        maxLength={200}
                    />
                </Form.Item>

                <Form.Item
                    name="status"
                    label="启用状态"
                    initialValue={flag === 'add' ? '0' : initValue?.status}>
                    <Select
                        value="0"
                        placeholder="请选择启用状态"
                        dropdownClassName={style.dropdown}>
                        <Option value="1">是</Option>
                        <Option value="0">否</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name="cron"
                    label="时间表达式："
                    initialValue={flag === 'add' ? '' : initValue?.cron}
                    rules={[
                        {required: true, message: `请输入半角字符的CRON表达`},
                    ]}>
                    <Input
                        placeholder="输入半角字符的CRON表达"
                        maxLength={500}
                    />
                </Form.Item>

                <Form.Item
                    name="startDate"
                    label="有效开始日："
                    initialValue={
                        flag !== 'add' && initValue?.startDate
                            ? dayjs(initValue.startDate, 'YYYY-MM-DD')
                            : ''
                    }>
                    <DatePicker placeholder="选择有效开始日" />
                </Form.Item>

                <Form.Item
                    name="endDate"
                    label="有效结束日："
                    initialValue={
                        flag !== 'add' && initValue?.endDate
                            ? dayjs(initValue.endDate, 'YYYY-MM-DD')
                            : ''
                    }>
                    <DatePicker placeholder="选择有效结束日" />
                </Form.Item>

                <Form.List name="task">
                    {(fields, {add, remove}) => (
                        <>
                            {fields.map(({key, name, ...restField}, index) => (
                                <div key={key} className={style.planContainer}>
                                    <Form.Item
                                        label={
                                            index === 0 ? '选择调度任务' : ''
                                        }
                                        name={[name, 'id']}
                                        {...restField}
                                        {...(index === 0
                                            ? layout
                                            : formItemLayoutWithOutLabel)}
                                        rules={[
                                            {
                                                required: true,
                                                message: '请选择调度任务',
                                            },
                                        ]}>
                                        <Select
                                            placeholder="请选择业务服务"
                                            dropdownClassName={style.dropdown}>
                                            {taskList.map(item => (
                                                <Option
                                                    value={item.id}
                                                    key={item.id}>
                                                    {item.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    {fields.length > 1 ? (
                                        <DeleteTwoTone
                                            className={style.deleteBtn}
                                            onClick={() => remove(name)}
                                        />
                                    ) : null}
                                </div>
                            ))}
                            <Button
                                onClick={() => add()}
                                icon={<PlusSquareOutlined />}
                                className={style.addPlanBtn}>
                                添加服务
                            </Button>
                        </>
                    )}
                </Form.List>

                <Form.Item
                    name="description"
                    label="说明："
                    initialValue={flag === 'add' ? '' : initValue?.description}>
                    <TextArea rows={3} placeholder="输入说明" maxLength={500} />
                </Form.Item>
            </Form>
        </Modal>
    );
}
